import {
  CONDITIONS_CONSUME_DETAIL_RESTORATION,
  CONDITIONS_CONSUME_NAVIGATOR_RESTORATION,
  CONDITIONS_DEFAULT_SORT,
  CONDITIONS_DEFAULT_TOTAL_PAGES,
  CONDITIONS_DELETE_PROJECTS,
  CONDITIONS_DELETE_PROJECTS_FAILURE,
  CONDITIONS_DELETE_PROJECTS_SUCCESS,
  CONDITIONS_FETCH_DETAIL,
  CONDITIONS_FETCH_DETAIL_FAILURE,
  CONDITIONS_FETCH_DETAIL_SUCCESS,
  CONDITIONS_FETCH_PROJECTS,
  CONDITIONS_FETCH_PROJECTS_FAILURE,
  CONDITIONS_FETCH_PROJECTS_SUCCESS,
  CONDITIONS_PAGE_SIZE,
  CONDITIONS_RENAME_PROJECT,
  CONDITIONS_RENAME_PROJECT_FAILURE,
  CONDITIONS_RENAME_PROJECT_SUCCESS,
  CONDITIONS_RESET_NAVIGATOR_VIEW,
  CONDITIONS_SELECT_DETAIL,
  CONDITIONS_SET_ANNOUNCEMENT,
  CONDITIONS_SET_DETAIL_RESTORATION,
  CONDITIONS_SET_DETAIL_SORT,
  CONDITIONS_SET_NAVIGATOR_RESTORATION,
  CONDITIONS_SET_PAGING,
  CONDITIONS_SET_SELECTED_IDS,
  CONDITIONS_SET_SORT,
} from './constants';
import { EMPTY_SUMMARY } from './components/conditionsDetailConstants';

function normalizeDetailPayload(payload = {}) {
  return {
    run: payload.run || null,
    recommendations: payload.recommendations || [],
    summary: payload.summary || EMPTY_SUMMARY,
  };
}

function updateCachedDetailName(detail, updatedRun = {}) {
  if (!detail?.run) return detail;

  return {
    ...detail,
    run: {
      ...detail.run,
      name: updatedRun.name,
      projectName: updatedRun.projectName,
    },
  };
}

function removeCachedDetails(detailsByProjectId, ids = []) {
  return Object.keys(detailsByProjectId || {}).reduce((acc, projectId) => {
    if (!ids.includes(projectId)) {
      acc[projectId] = detailsByProjectId[projectId];
    }
    return acc;
  }, {});
}

function renameProjectSuccess(state, { id, updatedRun }) {
  return {
    ...state,
    isLoading: false,
    runs: state.runs.map((run) => (run.id === id ? { ...run, name: updatedRun.name, projectName: updatedRun.projectName } : run)),
    detailByProjectId: {
      ...state.detailByProjectId,
      ...(state.detailByProjectId[id] ? { [id]: updateCachedDetailName(state.detailByProjectId[id], updatedRun) } : {}),
    },
  };
}

function deleteProjectsSuccess(state, { ids }) {
  const detailHasSettledByProjectId = Object.keys(state.detailHasSettledByProjectId || {}).reduce((acc, projectId) => {
    if (!ids.includes(projectId)) {
      acc[projectId] = state.detailHasSettledByProjectId[projectId];
    }
    return acc;
  }, {});

  return {
    ...state,
    isLoading: false,
    runs: state.runs.filter((run) => !ids.includes(run.id)),
    totalItems: Math.max(0, state.totalItems - ids.length),
    selectedIds: [],
    detailCurrentProjectId: ids.includes(state.detailCurrentProjectId) ? null : state.detailCurrentProjectId,
    detailByProjectId: removeCachedDetails(state.detailByProjectId, ids),
    detailHasSettledByProjectId,
    announcement: `${ids.length} condition search${ids.length !== 1 ? 'es' : ''} deleted.`,
  };
}

function selectCachedDetail(state, id) {
  return {
    ...state,
    detailCurrentProjectId: id,
    detailIsLoading: false,
    detailError: '',
    detailHasSettledByProjectId: {
      ...state.detailHasSettledByProjectId,
      [id]: true,
    },
  };
}

function fetchDetailSuccess(state, payload) {
  const detail = normalizeDetailPayload(payload);

  return {
    ...state,
    detailIsLoading: false,
    detailError: '',
    detailCurrentProjectId: payload.id,
    detailByProjectId: {
      ...state.detailByProjectId,
      [payload.id]: detail,
    },
    detailHasSettledByProjectId: {
      ...state.detailHasSettledByProjectId,
      [payload.id]: true,
    },
  };
}

function resetNavigatorView(state) {
  return {
    ...state,
    runs: [],
    sort: CONDITIONS_DEFAULT_SORT,
    selectedIds: [],
    announcement: '',
    error: '',
    isLoading: false,
    projectsHasSettledOnce: false,
    page: 0,
    pageSize: CONDITIONS_PAGE_SIZE,
    totalItems: 0,
    totalPages: CONDITIONS_DEFAULT_TOTAL_PAGES,
    navigatorRestoration: null,
  };
}

export const initialState = {
  runs: [],
  sort: CONDITIONS_DEFAULT_SORT,
  selectedIds: [],
  announcement: '',
  error: '',
  isLoading: false,
  projectsHasSettledOnce: false,
  page: 0,
  pageSize: CONDITIONS_PAGE_SIZE,
  totalItems: 0,
  totalPages: CONDITIONS_DEFAULT_TOTAL_PAGES,
  detailCurrentProjectId: null,
  detailByProjectId: {},
  detailHasSettledByProjectId: {},
  detailIsLoading: false,
  detailError: '',
  detailSortByProjectId: {},
  navigatorRestoration: null,
  detailRestoration: null,
};

// eslint-disable-next-line default-param-last
export default function reducer(state = initialState, action) {
  switch (action.type) {
    case CONDITIONS_FETCH_PROJECTS:
      return { ...state, isLoading: true, projectsHasSettledOnce: false, error: '' };
    case CONDITIONS_FETCH_PROJECTS_SUCCESS:
      return {
        ...state,
        isLoading: false,
        projectsHasSettledOnce: true,
        error: '',
        runs: action.payload.items || [],
        totalItems: action.payload.totalItems || 0,
        totalPages: action.payload.totalPages || CONDITIONS_DEFAULT_TOTAL_PAGES,
      };
    case CONDITIONS_FETCH_PROJECTS_FAILURE:
      return {
        ...state,
        isLoading: false,
        projectsHasSettledOnce: true,
        error: 'Condition searches could not be loaded. Check that the Condition Navigator backend is running.',
      };
    case CONDITIONS_SET_SORT:
      return { ...state, sort: action.payload, page: 0 };
    case CONDITIONS_SET_PAGING:
      return { ...state, ...action.payload };
    case CONDITIONS_SET_SELECTED_IDS:
      return { ...state, selectedIds: action.payload };
    case CONDITIONS_SET_ANNOUNCEMENT:
      return { ...state, announcement: action.payload };
    case CONDITIONS_RESET_NAVIGATOR_VIEW:
      return resetNavigatorView(state);
    case CONDITIONS_RENAME_PROJECT:
    case CONDITIONS_DELETE_PROJECTS:
      return { ...state, isLoading: true, error: '' };
    case CONDITIONS_RENAME_PROJECT_SUCCESS:
      return renameProjectSuccess(state, action.payload);
    case CONDITIONS_RENAME_PROJECT_FAILURE:
      return { ...state, isLoading: false, error: 'Condition search could not be renamed.' };
    case CONDITIONS_DELETE_PROJECTS_SUCCESS:
      return deleteProjectsSuccess(state, action.payload);
    case CONDITIONS_DELETE_PROJECTS_FAILURE:
      return { ...state, isLoading: false, error: 'Selected condition searches could not be deleted.' };
    case CONDITIONS_FETCH_DETAIL:
      return {
        ...state,
        detailCurrentProjectId: action.payload?.id || null,
        detailHasSettledByProjectId: {
          ...state.detailHasSettledByProjectId,
          ...(action.payload?.id ? { [action.payload.id]: false } : {}),
        },
        detailIsLoading: true,
        detailError: '',
      };
    case CONDITIONS_SELECT_DETAIL: {
      return selectCachedDetail(state, action.payload.id);
    }
    case CONDITIONS_FETCH_DETAIL_SUCCESS:
      return fetchDetailSuccess(state, action.payload);
    case CONDITIONS_FETCH_DETAIL_FAILURE:
      return {
        ...state,
        detailIsLoading: false,
        detailHasSettledByProjectId: {
          ...state.detailHasSettledByProjectId,
          ...(state.detailCurrentProjectId ? { [state.detailCurrentProjectId]: true } : {}),
        },
        detailError: 'Condition search could not be loaded.',
      };
    case CONDITIONS_SET_DETAIL_SORT:
      if (!action.payload.projectId) {
        return state;
      }
      return {
        ...state,
        detailSortByProjectId: {
          ...state.detailSortByProjectId,
          [action.payload.projectId]: {
            sortBy: action.payload.sortBy,
            sortOrder: action.payload.sortOrder,
          },
        },
      };
    case CONDITIONS_SET_NAVIGATOR_RESTORATION:
      return { ...state, navigatorRestoration: action.payload };
    case CONDITIONS_CONSUME_NAVIGATOR_RESTORATION:
      return { ...state, navigatorRestoration: null };
    case CONDITIONS_SET_DETAIL_RESTORATION:
      return { ...state, detailRestoration: action.payload };
    case CONDITIONS_CONSUME_DETAIL_RESTORATION:
      return { ...state, detailRestoration: null };
    default:
      return state;
  }
}
